import { onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useMobileShell } from "@/composables/useMobileShell";
import { isElectronShell, isStandaloneDisplay, nativeAppMode } from "@/utils/nativeAppMode";

/**
 * Makes the phone shell (< 960px) and the installed PWA behave like a native app, not a web page.
 *
 * - Browser history never grows: `plugins/1.router` writes every push as a replace while
 *   `nativeAppMode` is on, so the iOS edge-swipe has no page to go to. The in-app back stack lives in
 *   `utils/inAppHistory.ts`; in-app back buttons go through `navigateBack()`. The Android system
 *   back becomes an in-app back, and never closes a dialog.
 * - `html.app-native` switches on the native-feel CSS in `native-app.scss` (no text selection,
 *   long-press callouts, tap highlights, scrollbars or horizontal overscroll navigation).
 * - The browser's context menu is suppressed; components' own `@contextmenu` handlers still run.
 *
 * ⚠️ NEVER BINDS `touchstart`. An edge-swipe blocker was considered and rejected: a global
 * `touchstart` `preventDefault` swallows taps (the flatpickr bug in `usePinchZoomGuard`), which would
 * kill the app-bar back button at the screen edge. It is not needed either: with replace-only
 * history the swipe has nothing to go back to.
 *
 * Desktop browser tabs and Electron are untouched.
 */

/** Places that keep the browser's context menu (copy/paste in fields, explicit opt-outs). */
const CONTEXT_MENU_ALLOWED = "input, textarea, [contenteditable]:not([contenteditable='false']), [data-allow-contextmenu]";

function onContextMenu(event: MouseEvent) {
  if (!nativeAppMode.value)
    return;

  if (event.target instanceof Element && event.target.closest(CONTEXT_MENU_ALLOWED))
    return;

  event.preventDefault();
}

export function useNativeAppMode() {
  const { isMobileShell } = useMobileShell();
  const standalone = ref(isStandaloneDisplay());
  const standaloneQuery = typeof window !== "undefined"
    ? window.matchMedia?.("(display-mode: standalone)")
    : undefined;

  function onDisplayModeChange() {
    standalone.value = isStandaloneDisplay();
  }

  watch(
    () => !isElectronShell() && (isMobileShell.value || standalone.value),
    active => {
      nativeAppMode.value = active;
      document.documentElement.classList.toggle("app-native", active);
    },
    { immediate: true },
  );

  onMounted(() => {
    standaloneQuery?.addEventListener?.("change", onDisplayModeChange);
    document.addEventListener("contextmenu", onContextMenu);
  });

  onBeforeUnmount(() => {
    standaloneQuery?.removeEventListener?.("change", onDisplayModeChange);
    document.removeEventListener("contextmenu", onContextMenu);
  });
}
